import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box } from '@mui/material';
import { handleLoanFeesError } from 'app/pages/loanApplication/LoanApplication.utils';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import CicReasonFormModal from 'app/shared/modules/CicReasonFormModal';
import { CIC_REASON_MODAL_CONTROLS_ID } from 'app/shared/modules/CicReasonFormModal/CicReasonFormModal.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { FeatureFlag } from 'constants/featureFlags';
import { LESection } from 'constants/loanApplication';
import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';
import { getPendingChangesInfo, getSectionEFeesValues, getTotalAmountFee } from 'utils/loanFees';

import { openDialogAction } from 'store/global/actions';
import {
  editLoanFeesPatchAction,
  getLoanCicFeeReasonsActionV2,
  getLoanFeesSummary,
  getLoanFeesViewAction,
} from 'store/loanApplications/actions';
import { selectCicChangeReasons, selectCureBaseline } from 'store/loanApplications/selectors';
import {
  selectCicFeeReasonOptions,
  selectFeeProviderOptions,
  selectFeeTypesOptionsWithoutSellerPaid,
} from 'store/system/selectors';

import FeeTableView from '../../../AccordionDetailsView/FeeTable';
import PendingChangesNumber from '../../../Label/PendingChangesNumber';
import TotalAmount from '../../../Label/TotalAmount';
import Controls from '../../Controls';
import FeeTable from '../../FeeTable';
import { getDefaultSchema, getEditFeeActionData, getFeesPatchRequest } from '../Section.utils';

import RecordingFeeTableHead from './RecordingFeeTableHead';
import { boxStyles } from './TaxesAndGovernmentFees.styles';
import { getInitialFormValues } from './TaxesAndGovernmentFees.utils';

const schema = object().shape({
  recordingFees: getDefaultSchema(),
  transferTaxes: getDefaultSchema(),
});

const TaxesAndGovernmentFees = ({
  cdSection,
  groups,
  isWithHeader,
  isNonBorrowerPaidFieldsIncluded,
  labelNameAmountSection,
  isLoading,
  setEditedSection,
  setIsLoading,
  parentRef,
}) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();
  const { applicationId } = useParams();

  const [{ recordingFees }, { items: transferTaxes }] = groups;

  const [{ pendingChangesNumber, isTotalAmountPending }, setPendingChangesInfo] = useState({});

  const isSFCicReasonEnabledFF = useFeatureToggle(FeatureFlag.EnableStraightforwardCicReason);

  const feesOptions = useSelector(selectFeeTypesOptionsWithoutSellerPaid);
  const feeProviderOptions = useSelector(selectFeeProviderOptions);
  const cicFeeReasons = useSelector(selectCicFeeReasonOptions);
  const feesWithReason = useSelector(selectCicChangeReasons);
  const cureBaseline = useSelector(selectCureBaseline);

  const initialFormValues = getInitialFormValues({
    recordingFees,
    feesOptions,
    transferTaxes,
    feeProviderOptions,
    cureBaseline,
  });

  const {
    form,
    setForm,
    handleChange,
    validationErrors = {},
    setSchema,
    setInForm,
    validateField,
    isFormChanged,
    validateForm,
    setIsTouched,
    userEditedFieldNamesRef,
  } = useForm(initialFormValues);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffect(() => {
    const pendingChangesInfo = getPendingChangesInfo(parentRef);
    setPendingChangesInfo(pendingChangesInfo);
  }, [form, parentRef]);

  useEffect(() => {
    setEditedSection(prev => ({ ...prev, isFormChanged, ref: parentRef }));
  }, [setEditedSection, isFormChanged, parentRef]);

  const totalAmount = useMemo(
    () => _.sumBy([...form.recordingFees, ...form.transferTaxes], getTotalAmountFee),
    [form],
  );

  const { recordingFeesTotal } = useMemo(() => {
    return getSectionEFeesValues({ fees: form.recordingFees, isEdit: true });
  }, [form.recordingFees]);

  const getIsCicReasonModalDisplayed = useCallback(
    (data, isCicReasonButtonTriggered, userEditedFieldNames) => {
      const actionData = getEditFeeActionData({
        data,
        loanId,
        cicFeeReasons,
        feesWithReason,
        cureBaseline,
        userEditedFieldNames,
        rowPaths: [
          ...form.recordingFees.map((_fee, index) => `recordingFees.[${index}]`),
          ...form.transferTaxes.map((_fee, index) => `transferTaxes.[${index}]`),
        ],
      });

      if (!isSFCicReasonEnabledFF || !cureBaseline) {
        return [false, actionData];
      }

      return [
        _.some(actionData.changes, i => {
          if (isCicReasonButtonTriggered) {
            return i.fee.availableReasons;
          }

          return i.fee.availableReasons && !i.fee.isIterationBPAmountEQ;
        }),
        actionData,
      ];
    },
    [
      cicFeeReasons,
      loanId,
      feesWithReason,
      cureBaseline,
      isSFCicReasonEnabledFF,
      form.recordingFees,
      form.transferTaxes,
    ],
  );

  const [isApplyCicReasonButtonDisplayed] = useMemo(
    () =>
      getIsCicReasonModalDisplayed(
        [...initialFormValues.recordingFees, ...initialFormValues.transferTaxes],
        true,
      ),
    [
      getIsCicReasonModalDisplayed,
      initialFormValues.recordingFees,
      initialFormValues.transferTaxes,
    ],
  );

  const onCancelForm = useCallback(() => {
    setEditedSection(null);
  }, [setEditedSection]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      const isApplyCicReason = e.currentTarget.dataset.isApplyCicReason === 'true';

      if (!(await validateForm({ form, noFormChangedValidate: isApplyCicReason }))) {
        return;
      }

      const [isSFCicReasonEnabled, actionData] = getIsCicReasonModalDisplayed(
        [...form.recordingFees, ...form.transferTaxes],
        isApplyCicReason,
        userEditedFieldNamesRef.current,
      );

      const action = actionData => {
        setIsLoading(true);

        return dispatch(editLoanFeesPatchAction(getFeesPatchRequest(actionData)))
          .then(() => {
            dispatch(getLoanFeesViewAction({ applicationId }));
            dispatch(getLoanFeesSummary({ applicationId }));

            return dispatch(getLoanCicFeeReasonsActionV2({ loanId }));
          })
          .then(() => {
            setEditedSection(null);
            setIsTouched(false);
          })
          .catch(({ response }) => handleLoanFeesError({ response, dispatch }))
          .finally(() => setIsLoading(false));
      };

      if (isSFCicReasonEnabled) {
        return dispatch(
          openDialogAction({
            header: 'Select CIC Reason',
            isRestyled: true,
            size: ModalSize.LARGE,
            withDivider: true,
            hasScrollY: true,
            body: dialogProps => (
              <CicReasonFormModal
                action={action}
                actionData={actionData}
                isCicReasonButtonTriggered={isApplyCicReason}
                {...dialogProps}
              />
            ),
            customButtons: <div className="w-full" id={CIC_REASON_MODAL_CONTROLS_ID} />,
          }),
        );
      }

      return action(actionData);
    },
    [
      userEditedFieldNamesRef,
      validateForm,
      form,
      setIsLoading,
      dispatch,
      loanId,
      applicationId,
      setEditedSection,
      setIsTouched,
      getIsCicReasonModalDisplayed,
    ],
  );

  useEffect(() => {
    parentRef.current.onCancelForm = onCancelForm;
    parentRef.current.onSubmit = onSubmit;
  }, [onCancelForm, onSubmit, parentRef]);

  const meta = useMemo(
    () => ({
      handleChange,
      isFormChanged,
      validateField,
      validateForm,
      setInForm,
      setForm,
      setIsTouched,
      validationErrors,
      setEditedSection,
    }),
    [
      handleChange,
      isFormChanged,
      validateField,
      validateForm,
      setInForm,
      setForm,
      setIsTouched,
      validationErrors,
      setEditedSection,
    ],
  );

  return (
    <>
      <Box sx={boxStyles}>
        <FeeTableView
          isFeeProviderColumnVisible={false}
          isLoading={isLoading}
          isNonBorrowerPaidFieldsIncluded={isNonBorrowerPaidFieldsIncluded}
          isWithHeader={isWithHeader}
          items={recordingFeesTotal}
          labelNameAmountSection={labelNameAmountSection}
        />

        <FeeTable
          isRecordingType
          customHeader={<RecordingFeeTableHead />}
          data={form.recordingFees}
          isFeeProviderColumnVisible={false}
          isLoading={isLoading}
          isManageAllowed={false}
          isNonBorrowerPaidFieldsIncluded={isNonBorrowerPaidFieldsIncluded}
          isWithHeader={isWithHeader}
          labelNameAmountSection={labelNameAmountSection}
          leSection={LESection.RECORDING_FEES}
          meta={meta}
          prefix="recordingFees"
        />
      </Box>

      <FeeTable
        data={form.transferTaxes}
        isLoading={isLoading}
        isNonBorrowerPaidFieldsIncluded={isNonBorrowerPaidFieldsIncluded}
        isWithHeader={isWithHeader}
        labelNameAmountSection={labelNameAmountSection}
        leSection={LESection.TRANSFER_TAXES}
        meta={meta}
        prefix="transferTaxes"
      />

      {createPortal(
        <TotalAmount isPending={isTotalAmountPending} value={totalAmount} />,
        document.getElementById(`total-loan-fee-section-${cdSection}`),
      )}

      {createPortal(
        <PendingChangesNumber value={pendingChangesNumber} />,
        document.getElementById(`pending-changes-number-${cdSection}`),
      )}

      <Controls
        isApplyCicReasonButtonDisplayed={isApplyCicReasonButtonDisplayed}
        isFormChanged={isFormChanged}
        isLoading={isLoading}
        onCancelForm={onCancelForm}
        onSubmit={onSubmit}
      />
    </>
  );
};

TaxesAndGovernmentFees.propTypes = {
  ...WithLoadingPropTypes,
  isNonBorrowerPaidFieldsIncluded: PropTypes.bool.isRequired,
  isWithHeader: PropTypes.bool.isRequired,
  cdSection: PropTypes.string.isRequired,
  setEditedSection: PropTypes.func.isRequired,
  parentRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  labelNameAmountSection: PropTypes.string,
};

export default TaxesAndGovernmentFees;
